import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Checkbox } from 'antd';
import { Slot } from '../../../Modules/Slot/Model/Slot';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';

const { Option } = Select;

interface Props {
  open: boolean;
  slotCount: number;
  onClose: () => void;
  onSubmit: (dto: Partial<Slot>) => Promise<void>;
}

const SlotBulkEditModal: React.FC<Props> = ({
  open,
  slotCount,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clearMidSingle, setClearMidSingle] = useState(false);
  const [clearMidCompare, setClearMidCompare] = useState(false);
  const [clearProductUrl, setClearProductUrl] = useState(false);
  const [clearCompareUrl, setClearCompareUrl] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* 셀러 목록 */
  const accounts = useAppSelector(selectAccounts);
  const sellers = accounts
    .filter(a => a.grade === 'SELLER')
    .sort((a, b) => a.username.localeCompare(b.username));

  const resetAndClose = () => {
    form.resetFields();
    setClearMidSingle(false);
    setClearMidCompare(false);
    setClearProductUrl(false);
    setClearCompareUrl(false);
    onClose();
  };

  const clearCheckboxStyle: React.CSSProperties = {
    display: 'flex',
    width: 'fit-content',
    marginTop: -18,
    marginBottom: 8,
  };

  return (
    <Modal
      open={open}
      title={`선택된 ${slotCount}개 슬롯 일괄 수정`}
      okText="저장"
      confirmLoading={submitting}
      onCancel={() => { if (!submitting) resetAndClose(); }}
      onOk={async () => {
        if (submitting) return;
        const values = await form.validateFields();
        const dto = Object.fromEntries(
          Object.entries(values).filter(([, v]) => v !== undefined && v !== ''),
        ) as Partial<Slot>;

        if (clearMidSingle) dto.mid_single = '';
        if (clearMidCompare) dto.mid_compare = '';
        if (clearProductUrl) dto.product_url = '';
        if (clearCompareUrl) dto.compare_url = '';

        if (Object.keys(dto).length === 0) {
          resetAndClose();
          return;
        }

        // word_keyword는 항상 '-'로 설정
        dto.word_keyword = '-';

        setSubmitting(true);
        try {
          await onSubmit(dto);
          resetAndClose();
        } finally {
          setSubmitting(false);
        }
      }}
      destroyOnClose
      bodyStyle={isMobile ? { maxHeight: '80vh', overflow: 'auto', overscrollBehavior: 'contain' as any } : undefined}
    >
      <p style={{ marginBottom: 16 }}>
        <span style={{ color: '#fa541c' }}>* 체크박스로 선택된 슬롯에만 적용됩니다.</span>
      </p>

      <Form form={form} layout="vertical">
        {/* 아이디 */}
        <Form.Item name="user" label="아이디">
          <Select
            placeholder="(변경 시에만 선택)"
            showSearch
            optionFilterProp="label"
            optionLabelProp="label"
            allowClear
            getPopupContainer={trigger => trigger.parentElement as HTMLElement}
            listHeight={isMobile ? 180 : 256}
            dropdownStyle={isMobile ? { maxHeight: '40vh', overflow: 'auto', overscrollBehavior: 'contain' as any } : undefined}
            dropdownMatchSelectWidth
          >
            {sellers.map(s => (
              <Option
                key={s.id}
                value={s.id}
                label={`${s.username} (${s.name})`}
              >
                {s.username} ({s.name})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="main_keyword" label="메인 키워드">
          <Input placeholder="(변경 시에만 입력)" />
        </Form.Item>

        <Form.Item name="mid_single" label="단일 MID">
          <Input
            disabled={clearMidSingle}
            placeholder="(변경 시에만 입력)"
          />
        </Form.Item>

        <Checkbox
          aria-label="단일 MID 기존 값 삭제"
          checked={clearMidSingle}
          onChange={(event) => {
            const checked = event.target.checked;
            setClearMidSingle(checked);
            if (checked) form.setFieldValue('mid_single', '');
          }}
          style={clearCheckboxStyle}
        >
          기존 값 삭제
        </Checkbox>

        <Form.Item name="mid_compare" label="가격비교 MID">
          <Input
            aria-label="가격비교 MID"
            disabled={clearMidCompare}
            placeholder="(변경 시에만 입력)"
          />
        </Form.Item>

        <Checkbox
          aria-label="가격비교 MID 기존 값 삭제"
          checked={clearMidCompare}
          onChange={(event) => {
            const checked = event.target.checked;
            setClearMidCompare(checked);
            if (checked) form.setFieldValue('mid_compare', '');
          }}
          style={clearCheckboxStyle}
        >
          기존 값 삭제
        </Checkbox>

        <Form.Item
          name="product_url"
          label="상품 URL"
          rules={[{ type: 'url', message: 'URL 형식이 아닙니다.' }]}
        >
          <Input disabled={clearProductUrl} placeholder="https://..." />
        </Form.Item>

        <Checkbox
          aria-label="상품 URL 기존 값 삭제"
          checked={clearProductUrl}
          onChange={(event) => {
            const checked = event.target.checked;
            setClearProductUrl(checked);
            if (checked) form.setFieldValue('product_url', '');
          }}
          style={clearCheckboxStyle}
        >
          기존 값 삭제
        </Checkbox>

        <Form.Item
          name="compare_url"
          label="가격비교 URL"
          rules={[{ type: 'url', message: 'URL 형식이 아닙니다.' }]}
        >
          <Input
            aria-label="가격비교 URL"
            disabled={clearCompareUrl}
            placeholder="https://..."
          />
        </Form.Item>

        <Checkbox
          aria-label="가격비교 URL 기존 값 삭제"
          checked={clearCompareUrl}
          onChange={(event) => {
            const checked = event.target.checked;
            setClearCompareUrl(checked);
            if (checked) form.setFieldValue('compare_url', '');
          }}
          style={clearCheckboxStyle}
        >
          기존 값 삭제
        </Checkbox>

        <Form.Item name="memo" label="메모">
          <Input.TextArea rows={3} placeholder="(변경 시에만 입력)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SlotBulkEditModal;
